<script setup lang="ts">
import logo from "@brand-logo"
import { useHead } from "@unhead/vue"
import { AppLink, Card, Icon } from "@/components/ui"
import { useTranslation } from "@/composables"

const { t, n } = useTranslation()

definePage({
  meta: {
    requiresAuth: false,
  },
})

useHead({
  title: t("verify_email_page.page_title"),
})
</script>

<template>
  <div class="min-h-screen bg-footer-bg text-primary font-heading flex items-center">
    <div class="mx-auto w-full max-w-3xl px-4 md:px-6">
      <div class="mb-4 flex justify-center">
        <img :src="logo" alt="Guestpix Logo" loading="lazy" class="h-10 w-auto max-w-full object-contain">
      </div>
      <Card :with-padding="false" border-radius="xl" class="mx-auto w-full max-w-xl p-5 shadow-xl sm:p-6 md:p-8 lg:p-10">
        <div class="flex flex-col gap-5">
          <div class="flex justify-center">
            <div class="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <Icon icon="Envelope" size="lg" />
            </div>
          </div>
          <div class="text-center">
            <h1 class="font-accent text-3xl leading-tight sm:text-4xl">
              {{ t("verify_email_page.title") }}
            </h1>
            <p class="mt-2 text-sm text-secondary-card-text font-body sm:text-base">
              {{ t("verify_email_page.subtitle") }}
            </p>
          </div>
          <div>
            <h2 class="text-xl font-accent sm:text-2xl">
              {{ t("verify_email_page.next_steps_title") }}
            </h2>
          </div>

          <ol class="space-y-3 text-sm text-secondary-card-text font-body">
            <li class="flex gap-3">
              <span class="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">{{ n(1) }}</span>
              <span>{{ t("verify_email_page.steps.check_inbox") }}</span>
            </li>
            <li class="flex gap-3">
              <span class="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">{{ n(2) }}</span>
              <span>{{ t("verify_email_page.steps.confirm_email") }}</span>
            </li>
            <li class="flex gap-3">
              <span class="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">{{ n(3) }}</span>
              <i18n-t keypath="verify_email_page.steps.return_to_finish" scope="global" tag="span">
                <template #link>
                  <AppLink class="text-link underline underline-offset-4" to="/event/dashboard">
                    {{ t("verify_email_page.steps.dashboard_link_label") }}
                  </AppLink>
                </template>
              </i18n-t>
            </li>
          </ol>

          <i18n-t
            keypath="verify_email_page.support_note"
            scope="global"
            tag="p"
            class="border-t border-gray-100 pt-4 text-xs text-secondary-card-text font-body sm:text-sm"
          >
            <template #email>
              <a class=" text-link underline underline-offset-4" href="mailto:customercare@guestpix.com">
                customercare@guestpix.com
              </a>
            </template>
          </i18n-t>
        </div>
      </Card>
    </div>
  </div>
</template>
